import { Divider, Group, Popover, SegmentedControl, Text } from '@byecode/ui'
import { BACKGROUND_TYPE, DIRECTION } from '@lighthouse/core'
import type { SendEmailActionConfig } from '@lighthouse/shared'
import { FilledInput, FillPickerPopoverDropdown, PARAGRAPH_WIDTH_TYPE } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React from 'react'

import { GapSizeInput } from '@/components/StyleSetting/AutoLayoutSetting/GapSizeInput'
import { PaddingInput } from '@/components/StyleSetting/AutoLayoutSetting/PaddingInput'
import { BorderField } from '@/components/StyleSetting/components/BorderField'
import { useColorSystemAction } from '@/hooks/useColorSystemAction'

import { FormItem } from '../../../Common/FormItem'

const bodyWidthOptions = [
    { value: PARAGRAPH_WIDTH_TYPE.NORMAL, label: '标准' },
    { value: PARAGRAPH_WIDTH_TYPE.WIDER, label: '较宽' },
    { value: PARAGRAPH_WIDTH_TYPE.FULL, label: '全宽' }
]

export const EmailStyle: React.FC<{ value: SendEmailActionConfig; onChange: (value: SendEmailActionConfig) => void }> = ({
    value,
    onChange
}) => {
    const colorSystemMethods = useColorSystemAction()

    return (
        <>
            <Divider color="var(--color-gray-200)" />
            <Group label="邮件样式">
                <FormItem label="邮件背景">
                    <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={168}>
                        <Popover.Target>
                            <FilledInput
                                onClear={() => {
                                    onChange({
                                        ...value,
                                        background: { type: BACKGROUND_TYPE.color }
                                    })
                                }}
                                value={value.background}
                                style={{ width: 180 }}
                            />
                        </Popover.Target>
                        <FillPickerPopoverDropdown
                            title="背景"
                            value={value.background}
                            onChange={background => {
                                onChange({ ...value, background })
                            }}
                            enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                            {...colorSystemMethods}
                        />
                    </Popover>
                </FormItem>
                <FormItem label="邮件边距">
                    <PaddingInput value={value.padding} onChange={padding => onChange({ ...value, padding })} disablePercent disableRem />
                </FormItem>
            </Group>
            <Divider color="var(--color-gray-200)" />
            <Group label="正文样式">
                <FormItem label="正文段落宽度">
                    <SegmentedControl
                        style={{ width: 180 }}
                        fullWidth
                        data={bodyWidthOptions}
                        value={value.body?.width}
                        onChange={width =>
                            onChange({
                                ...value,
                                body: { ...value.body, width: width as SendEmailActionConfig['body']['width'] }
                            })
                        }
                    />
                </FormItem>
                <FormItem label="正文背景">
                    <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={168}>
                        <Popover.Target>
                            <FilledInput
                                value={value.body?.background}
                                onClear={() => {
                                    onChange({
                                        ...value,
                                        body: {
                                            ...value.body,
                                            background: { type: BACKGROUND_TYPE.color }
                                        }
                                    })
                                }}
                                style={{ width: 180 }}
                            />
                        </Popover.Target>
                        <FillPickerPopoverDropdown
                            title="背景"
                            value={value.body?.background}
                            onChange={background => {
                                onChange({ ...value, body: { ...value.body, background } })
                            }}
                            enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                            {...colorSystemMethods}
                        />
                    </Popover>
                </FormItem>

                <BorderField
                    label={<Text>正文描边</Text>}
                    value={value.body?.border}
                    onChange={border => {
                        if (isDeepEqual(border, value.body?.border)) {
                            return
                        }
                        onChange({ ...value, body: { ...value.body, border } })
                    }}
                />

                <FormItem label="正文边距">
                    <PaddingInput
                        value={value.body?.padding}
                        onChange={padding => onChange({ ...value, body: { ...value.body, padding } })}
                        disablePercent
                        disableRem
                    />
                </FormItem>

                <FormItem label="正文间距">
                    <GapSizeInput
                        direction={DIRECTION.vertical}
                        value={value.body?.gap}
                        onChange={gap => onChange({ ...value, body: { ...value.body, gap } })}
                        disablePercent
                        disableRem
                    />
                </FormItem>
            </Group>
        </>
    )
}
